import { defineAsyncComponent } from 'vue';
import type { Composition } from '@spreadgroup/common-partner-lib/composition';
import dialogService from 'commons/dialogs/wrapper/dialogService';
import type { SampleOrderSource } from './sampleOrderTracking';

/**
 * The journey's UI is loaded on demand. Partners who never order a sample never download it, and —
 * just as importantly — an entry point that imports this seam does not drag the whole journey into
 * its own module graph.
 */
const SampleOrderDialog = defineAsyncComponent(
  () => import('src/app/partnerarea/sampleOrder/SampleOrderDialog.vue'),
);

/**
 * **The one way into the sample order journey.**
 *
 * Every entry point calls this with a composition and the scope it stands in — the sellable list
 * (DEV-231126), SMART product settings (DEV-231128), the dashboard (DEV-231131) and shop settings
 * (DEV-231132). Adding an entry point means calling this function; it must not mean changing the
 * journey.
 *
 * `scope` picks the sample shop (DEV-234264). It cannot come from the composition, which may sit in
 * both scopes; the entry point knows which one the partner is ordering for.
 *
 * `source` is the one exception, and it is not journey input: it is a tracking label (DEV-231272),
 * needed because completion per entry point is the whole point of the funnel. **Nothing in the
 * journey may branch on it.**
 *
 * Resolves when the partner is handed over to the checkout, rejects when they cancel — dialog
 * semantics, so a caller that does not care about either can ignore the promise.
 */
export const openSampleOrderJourney = (
  composition: Composition,
  { scope, source }: { scope: string; source: SampleOrderSource },
) =>
  dialogService.openDialog(
    SampleOrderDialog,
    { composition, scope, source },
    // A full-viewport journey: the dialog's own close cross would collide with the top bar's
    // Cancel, and there is no "outside" to click.
    { fullSize: true, displayClose: false, staticBackdrop: true },
  );
